import {
	ChangeDetectionStrategy,
	Component,
	computed,
	Inject,
	input,
	OnDestroy,
	OnInit,
	signal,
} from '@angular/core';
import {
	CourseSearchBarComponent,
	MainHeaderComponent,
	PrimaryBlueBtnComponent,
	StaticCardComponent,
} from '@web/shared/ui';
import {
	CLEARING_CARD,
	DEGREE_LEVEL_OPTIONS,
	DIRECTORY_CARD,
} from '../../static/courses-hub.properties';
import { getDegreeLevelOptions } from '../../utils/utils';
import { ClearingService, Environment, SeoMetadataService } from '@uc/utilities';
import { HeadingComponent } from '@uc/shared/ui';
import { CourseDegreeCopy } from '../../models/course-degree-hub.models';
import { CoursesHubLinksComponent } from '../../components/courses-hub-links/courses-hub-links.component';
import { DegreeSubjectCategoryComponent } from '../../components/degree-subject-category/degree-subject-category.component';
import { DegreeLevelOption } from '@uc/web/shared/data-models';
import { DegreeLevelService } from '@uc/web/shared/data-access';
import { Router } from '@angular/router';
import { HECoSSubjectValidatorService } from '@uc/web/core';
import { getCourseSearchSegments } from '../../utils/route.utils';
import { CourseSearchDataService } from '../../data-access/course-search-data.service';
import { getDegreeLevelType } from '../../utils/course-search-payload.utils';
import { NgClass } from '@angular/common';
import { TopDegreeSubjectsSkeletonComponent } from '../../components/top-degree-subjects-skeleton/top-degree-subjects-skeleton.component';

@Component({
	selector: 'uc-courses-degrees-hubs',
	templateUrl: './course-degree-hub.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		MainHeaderComponent,
		CourseSearchBarComponent,
		HeadingComponent,
		CoursesHubLinksComponent,
		StaticCardComponent,
		DegreeSubjectCategoryComponent,
		PrimaryBlueBtnComponent,
		NgClass,
		TopDegreeSubjectsSkeletonComponent,
	],
})
export class CourseDegreeHubComponent implements OnInit, OnDestroy {
	data = input.required<CourseDegreeCopy>();
	clearingCard = signal({ ...CLEARING_CARD });
	directoryCard = { ...DIRECTORY_CARD };

	shouldFetchDegreeLevelHubLinks = computed(() => this.data().showDegreeLevelHubLinks);
	viewAllSubjectsLink = computed(() => this._getDirectoryPath());
	topDegreeSubjects = computed(() => this._courseSearchDataSrv.topDegreeSubjects());
	degreeLevelHubLinks = computed(() => {
		if (!this.shouldFetchDegreeLevelHubLinks()) return;

		const links = this._courseSearchDataSrv.degreeLevelHubLinks();
		if (!links) return;
		const toRemove = new Set([
			'/courses/clearing',
			'/courses/degrees/undergraduate',
			'/courses/degrees/postgraduate',
		]);

		return links.filter((link) => !toRemove.has(link.link));
	});
	link = computed(() => this._getSubjectPath());
	headerTitle = computed(() =>
		this.data().headerData.title.replace('[YEAR]', this.clearingYear),
	);

	isLoading = computed(() => this._courseSearchDataSrv.isLoading());

	clearingYear = this._clearingSrv.getYear().toString();
	degreeLevelOptions: DegreeLevelOption[] = [];
	clearingEnabled!: boolean;

	constructor(
		@Inject('environment') private _environment: Environment,
		private _router: Router,
		private _seoSrv: SeoMetadataService,
		private _clearingSrv: ClearingService,
		private _degreeLevelSrv: DegreeLevelService,
		private _courseSearchDataSrv: CourseSearchDataService,
		private _hecosSubjectValidatorSrv: HECoSSubjectValidatorService,
	) {
		this.clearingEnabled = this._environment.clearingEnabled;
	}

	ngOnInit(): void {
		if (this.shouldFetchDegreeLevelHubLinks()) {
			this._courseSearchDataSrv.fetchDegreeLevelHubLinks('DEGREE');
		}

		this._initialiseDegreeLevelOptions();
		this._fetchTopDegreeSubjects();
		this._updateSeoMetadata();
		this._includeClearingOption();
		this._updateClearingCTACopy();
		this._updateClearingIntroCopy();
		this._updateDirectoryCTACopy();
	}

	ngOnDestroy(): void {
		this._courseSearchDataSrv.degreeLevelHubLinks.set(null);
		this._courseSearchDataSrv.topDegreeSubjects.set(null);
	}

	async onSearch(autosuggest: { searchTerm: string; degree?: string }) {
		if (!autosuggest.searchTerm) return;

		const { degree, searchTerm } = autosuggest;

		const subject = searchTerm.replace(/ /g, '-').toLowerCase();
		const isHECoSSubject =
			await this._hecosSubjectValidatorSrv.isHECoSSubject(subject);

		const segments = getCourseSearchSegments(isHECoSSubject, degree, subject);

		this._router.navigate([...segments], {
			queryParams: isHECoSSubject ? {} : { search: subject },
		});
	}

	private _updateClearingCTACopy() {
		const clearingYear = this._clearingSrv.getYear().toString();
		const isClearing = this._clearingSrv.isClearing();

		const clearingStatusMessage = isClearing
			? 'Clearing is open! '
			: `Clearing opens July ${clearingYear}.`;

		const description = this.clearingCard().desc.replace(
			'[CLEARING_STATUS]',
			clearingStatusMessage,
		);
		this.clearingCard().desc = description;
	}

	private _updateDirectoryCTACopy() {
		const directoryCard = this.data().directoryCard;
		if (this.data().showClearingAndDirectoryCTA && directoryCard) {
			this.directoryCard.title = directoryCard.title;
			this.directoryCard.link = directoryCard.link;
		}
	}

	private _updateClearingIntroCopy() {
		if (this.data().degreeLevel === 'clearing') {
			const clearingYear = this._clearingSrv.getYear().toString();
			const title = this.data().pageIntro.title.replace('[YEAR]', clearingYear);
			const description = this.data().pageIntro.description.replace(
				/\[YEAR\]/g,
				clearingYear,
			);
			this.data().pageIntro.title = title;
			this.data().pageIntro.description = description;
		}
	}

	private _includeClearingOption() {
		if (!this.clearingEnabled) return;

		const clearing = DEGREE_LEVEL_OPTIONS.find(
			(option) => option.label === 'Clearing',
		);

		if (clearing && this.data().degreeLevel === 'clearing') {
			this.data().degreeLevelLabels.unshift('Clearing');
			this.degreeLevelOptions.unshift(clearing);
		} else {
			clearing && this.degreeLevelOptions.push(clearing);
		}
	}

	private _initialiseDegreeLevelOptions() {
		this.degreeLevelOptions = getDegreeLevelOptions(this.data().degreeLevelLabels);
		const selectedDegreeLevel = this.degreeLevelOptions.find(
			(option) => this.data().degreeLevel === option.link,
		);

		this._degreeLevelSrv.degreeLevels.set(this.degreeLevelOptions);
		this._degreeLevelSrv.selectedDegreeLevel.set(
			selectedDegreeLevel ?? this.degreeLevelOptions[0],
		);
	}

	private _fetchTopDegreeSubjects() {
		const degreeLevel = this.data().degreeLevel;
		const isClearing = degreeLevel === 'clearing';
		// TODO: handle clearing directory subjects
		const degreeLevelType = isClearing
			? 'ALL_UNDERGRADUATE'
			: getDegreeLevelType(degreeLevel);

		this._courseSearchDataSrv.fetchTopDegreeSubjects('DEGREE', degreeLevelType);
	}

	private _getSubjectPath() {
		const degreeLevel = this.data().degreeLevel;

		if (degreeLevel === 'clearing') {
			return this.clearingEnabled ? '/courses/clearing' : '/courses/degrees';
		}

		return degreeLevel === 'degrees'
			? `/courses/degrees`
			: `/courses/degrees/${degreeLevel}`;
	}

	private _getDirectoryPath() {
		const degreeLevel = this.data().degreeLevel;

		return degreeLevel === 'degrees' ? `/directory` : `/directory/${degreeLevel}`;
	}

	private _updateSeoMetadata() {
		const year = this._clearingSrv.getYear().toString();
		const title = this.data().metadata.title.replace('[YEAR]', year);
		const description = this.data().metadata.description.replace('[YEAR]', year);
		this._seoSrv.updateTitle(title);
		this._seoSrv.updateDescription(description);
	}
}
